import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export default function SignupPromptModal() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user has already seen the modal or dismissed it
    const hasSeenModal = sessionStorage.getItem("signup_modal_seen");
    if (hasSeenModal) return;

    // Show modal after 15-20 seconds (random between 15-20)
    const delay = Math.floor(Math.random() * 5000) + 15000; // 15-20 seconds
    const timer = setTimeout(() => {
      setIsOpen(true);
      sessionStorage.setItem("signup_modal_seen", "true");
    }, delay);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSignup = () => {
    setIsOpen(false);
    navigate("/register");
  };

  const benefits = [
    "memorarea procentajelor de parcurgere;",
    "continui de unde ai rămas atunci când revii pe website;",
    "vei ști când ești 100% pregătit/ă să iei examenul teoretic."
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={handleClose}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

          {/* Modal */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <Card className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-blue-500/30 shadow-2xl shadow-blue-500/10">
              <CardContent className="p-6">
                {/* Close button */}
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>

                {/* Header */}
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <UserPlus className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white font-['Archivo'] mb-2">
                    Creează-ți cont pe AUTO CORA!
                  </h2>
                  <p className="text-slate-400">
                    Îți recomandăm să îți creezi un cont pentru a beneficia de toate funcțiile.
                  </p>
                </div>

                {/* Benefits */}
                <div className="mb-6">
                  <p className="text-slate-300 mb-4 text-sm">
                    Printre cele mai importante funcții se numără:
                  </p>
                  <ul className="space-y-2">
                    {benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full flex items-center justify-center bg-green-500/20 flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-green-400" />
                        </div>
                        <span className="text-slate-300 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Free notice */}
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-6 text-center">
                  <p className="text-green-400 font-medium">
                    Crearea unui cont este gratuită!
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                  >
                    Nu acum
                  </Button>
                  <Button
                    onClick={handleSignup}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Da, vreau cont!
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
